import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../services/hooks/apiHook';
import { IAllJurisdictions, UpdateJurisdictionPayload } from '../services/Interfaces/IAccountService';
import { useAuth } from '../store/auth';
import { errorMessageAdapter } from '../adapters';
import { errorToast, successToast } from '../utils/toasts';
import i18n from '../plugins/i18n/i18n';
import { InstancesServices } from '../services/InstancesServices';

const jurisdictionsCache: { key: string | null; data: IAllJurisdictions[] } = {
  key: null,
  data: []
};

let jurisdictionsPromise: Promise<IAllJurisdictions[] | undefined> | null = null;

export const useJurisdictions = () => {
  const [jurisdictions, setJurisdictions] = useState<IAllJurisdictions[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const api = useApi();
  const { token } = useAuth();

  const fetchJurisdictions = useCallback(
    async (force = false) => {
      if (!token) {
        setJurisdictions([]);
        jurisdictionsCache.key = null;
        jurisdictionsCache.data = [];
        return [];
      }

      const key = `${token}|${i18n.language.toLowerCase()}`;

      if (!force) {
        if (jurisdictionsCache.key === key && jurisdictionsCache.data.length) {
          setJurisdictions(jurisdictionsCache.data);
          setLoading(false);
          return jurisdictionsCache.data;
        }

        if (jurisdictionsPromise) {
          setLoading(true);
          const data = await jurisdictionsPromise;
          setLoading(false);
          return data ?? [];
        }
      } else {
        jurisdictionsCache.key = null;
        jurisdictionsCache.data = [];
        jurisdictionsPromise = null;
      }

      setLoading(true);

      const request = InstancesServices.getAllJurisdictions(api, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Language': i18n.language.toLowerCase()
        }
      })
        .then((response) => {
          const data = response?.data?.data ?? [];
          jurisdictionsCache.key = key;
          jurisdictionsCache.data = data;
          setJurisdictions(data);
          return data;
        })
        .catch((error) => {
          errorToast(errorMessageAdapter(error));
          jurisdictionsCache.key = key;
          jurisdictionsCache.data = [];
          setJurisdictions([]);
          return [];
        })
        .finally(() => {
          setLoading(false);
          jurisdictionsPromise = null;
        });

      jurisdictionsPromise = request;
      return request;
    },
    [api, token, i18n.language]
  );

  const updateJurisdiction = async (payload: UpdateJurisdictionPayload) => {
    setLoading(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Language': i18n.language.toLowerCase()
        }
      };

      await InstancesServices.updateJuridiction(api, payload, config);
      await fetchJurisdictions(true);

      successToast(i18n.t('environment.jurisdictionUpdatedSuccess'));
    } catch (error: any) {
      console.error('Erro ao atualizar taxa:', error);
      errorToast(error?.response?.data?.error[0] || i18n.t('environment.errorUpdatingJurisdiction'));
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    void fetchJurisdictions();
  }, [fetchJurisdictions]);

  return { jurisdictions, loading, updateJurisdiction, refreshJurisdictions: fetchJurisdictions };
};
